import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from "../../services/api";
import { useHistory } from 'react-router-dom';
import { deselectEvent } from '../../redux/actions/events';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { connect } from 'react-redux';
import Modal from '../generic/modal';
import { RevButton } from '../ds';
import { isEventPublished } from '../../helpers/eventPublication';

const EventCodeInput = ({ onCopyCode, initialEventCode, event, ...props }) => {
    const { t } = useTranslation();
    const [originalEventCode] = useState(initialEventCode || '');
    const [newEventCode, setNewEventCode] = useState('');
    const [modalActive, setModalActive] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [isAvailable, setIsAvailable] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false); // Nuevo estado para manejar la edición
    const history = useHistory();
    const published = isEventPublished(event);

    const validateEventCode = useCallback((code) => {
        const regex = /^[A-Z0-9]{8}$/;
        return regex.test(code);
    }, []);

    const checkAvailability = useCallback(async (code) => {
        setLoading(true);
        try {
            const response = await api.get('/check-event-code-availability/' + code);
            setIsAvailable(response.data);
        } catch (error) {
            setIsAvailable(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (validateEventCode(newEventCode)) {
            checkAvailability(newEventCode);
        } else {
            setIsAvailable(null);
        }
    }, [newEventCode, validateEventCode, checkAvailability]);

    const handleInputChange = (e) => {
        let value = e.target.value.toUpperCase();
        value = value.replace(/[^A-Z0-9]/g, '');

        if (value.length > 8) {
            value = value.slice(0, 8);
        }
        setNewEventCode(value);
        setIsValid(validateEventCode(value));
    };

    const handleSaveCode = async () => {
        if (!isValid || !isAvailable) return;

        try {
            setIsSaving(true);
            const response = await api.get(`/change-event-code/${originalEventCode}/${newEventCode}`);
            if (response.data) {
                props.deselectEvent();
                history.push(`/events`);
            } else {
                console.error(t('Failed to change event code'));
            }
        } catch (error) {
            console.error(t('An error occurred while changing the event code'), error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setNewEventCode(''); // Limpia el código nuevo y vuelve al original
    };

    const handleConfirmEdit = async () => {
        setModalActive(false);
        await handleSaveCode();
        setIsEditing(false);
    };

    return (
        <div>
            <div className="rev-field-row rev-field-row--gap">
                <input
                    id="eventCode"
                    className={`rev-input rev-input--code rev-grow${!isEditing ? ' rev-input--readonly' : ''}`}
                    type="text"
                    placeholder={t('8 characters, letters and numbers, uppercase')}
                    value={isEditing ? newEventCode : originalEventCode}
                    onChange={handleInputChange}
                    disabled={!isEditing || loading}
                    maxLength={8}
                />
                {isEditing ? (
                    <>
                        <RevButton
                            variant="secondary"
                            icon
                            onClick={(e) => {
                                e.preventDefault();
                                setModalActive(true);
                            }}
                            disabled={!isValid || !isAvailable || loading}
                        >
                            <span className="material-icons">check</span>
                        </RevButton>
                        <RevButton
                            variant="danger"
                            icon
                            onClick={(e) => {
                                e.preventDefault();
                                handleCancelEdit();
                            }}
                        >
                            <span className="material-icons">close</span>
                        </RevButton>
                    </>
                ) : (
                    <RevButton
                        variant="secondary"
                        icon
                        onClick={(e) => {
                            e.preventDefault();
                            handleEditClick();
                        }}
                        disabled={event?.hasOrders || event?.hasCarts || published}
                        title={
                            published
                                ? t('event.cannotEditCodePublished')
                                : (event?.hasOrders || event?.hasCarts) ? t('event.cannotEditCodeWithOrders') : ''
                        }
                    >
                        <span className="material-icons">edit</span>
                    </RevButton>
                )}
                <RevButton
                    variant="secondary"
                    onClick={(e) => {
                        e.preventDefault();
                        onCopyCode(originalEventCode);
                    }}
                >
                    <span className="material-icons">content_copy</span>
                    {t('event.basicData.code.copyButton')}
                </RevButton>
            </div>
            {loading && <p className="rev-help">{t('Checking availability')}...</p>}
            {isAvailable !== null && !isSaving && !loading && (
                isAvailable
                    ? <p className="rev-help">{t('The code {{code}} is available', { code: newEventCode || originalEventCode })}</p>
                    : <p className="rev-error">{t('The code {{code}} is not available', { code: newEventCode || originalEventCode })}</p>
            )}
            {isSaving && <p className="rev-help">{t('Saving')}...</p>}
            {modalActive &&
                <Modal
                    showCancelButton
                    showConfirmButton
                    title={t('Change event code')}
                    message={t('Are you sure you want to change the event code? Other shared codes will NO longer be valid')}
                    cancelButtonText={t('generic.alertButton.cancel')}
                    confirmButtonText={t('Yes')}
                    onConfirm={(e) => { e.preventDefault(); handleConfirmEdit() }}
                    onCancel={(e) => { e.preventDefault(); setModalActive(false); }}
                />
            }
        </div>
    );
};

const mapStateToProps = state => (
    {
        event: state.eventsReducer.selectedEvent,
    }
);

const mapDispatchToProps =
{
    deselectEvent,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EventCodeInput));
